import "./MobileWarning.css";
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import {
  auth,
  signInWithGoogle,
  logout,
  db
} from "./auth";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp
} from "firebase/firestore";

import EditVibeMainPage from "./components/EditVibeMainPage";
import EditVibeLayout from "./components/EditVibeLayout";
import AdminDashboard from "./components/AdminDashboard"; // Admin tasks
import EditVibeTool from "./components/EditVibeTool"; // Import your new tool
import "./App.css";
import editvibelogo1 from "./assets/Editvibelogo1.svg";
import PurchaseSuccess from './components/PurchaseSuccess';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AboutPage from './components/AboutPage';
import { Link } from 'react-router-dom';


// detect which hostname we’re on
const host = window.location.hostname;
const isAppSubdomain = host === "app.editvibe.ai";

// New helper function to validate email addresses with allowed domains
function isValidEmail(email) {
  const basicRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!basicRe.test(email)) {
    return false;
  }
  // Allowed domains – adjust as needed
  const allowedDomains = ['gmail.com', 'yahoo.com', 'outlook.com'];
  const domain = email.split('@')[1].toLowerCase();
  return allowedDomains.includes(domain);
}

const App = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign in / up states
  const [isSignUp, setIsSignUp] = useState(false);
  const [signUpStep, setSignUpStep] = useState(1); // We'll have 2 steps for sign-up

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginStep, setLoginStep] = useState(1);

  // ——— Turnstile helper ———
  const getTurnstileToken = () => {
    return window.turnstile ? window.turnstile.getResponse() : "";
  
  };

  async function verifyCaptcha(token) {
    const res = await fetch(
      "https://us-central1-editvibe-90968.cloudfunctions.net/verifyTurnstile",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      }
    );
    const json = await res.json();
    return json.success;
  }
  

   // ─── Turnstile loader ─────────────────────────────────────────────────────────
   useEffect(() => {
    // avoid injecting twice
    if (!document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]')) {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);
  // 2) once the form & div are in the DOM, render widget
  useEffect(() => {
    if (!window.turnstile) return;
    function maybeRender(id) {
      const el = document.getElementById(id);
      if (el && !el.dataset.rendered) {
        window.turnstile.render(el, { sitekey: "0x4AAAAAABaV9SdFLBkhvmJ4" });
        el.dataset.rendered = "true";
      }
    }
    maybeRender("cf-widget-login");
    maybeRender("cf-widget-signup");
  }, [signUpStep, isSignUp, loginStep]);

  // ============================
  // Listen for auth state changes
  // ============================
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);

        // If user doc doesn't exist, create with default
        const docSnap = await getDoc(userDocRef);
        if (!docSnap.exists()) {
          await setDoc(userDocRef, {
            credits: 1150,
            plan: "free",
            createdAt: serverTimestamp(),
            email: currentUser.email,
            userId: currentUser.uid, // Store the Firebase UID as userId
            hasSeenTour: false        // ← add this
          });
          console.log("New user doc created with 1000 free credits.");
        } else {
          console.log("Existing user doc found.");
        }

        // Listen for real-time updates
        const unsubscribeSnapshot = onSnapshot(userDocRef, (snapshot) => {
          if (snapshot.exists()) {
            setUserData(snapshot.data());
          }
        });

        setLoading(false);
        return () => unsubscribeSnapshot();
      } else {
        setUserData(null);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // ============================
// ─── Sign In with Email ─────────────────────────────────────────
const handleEmailLogin = async () => {
  // 1) grab the Turnstile token
  const token = getTurnstileToken();
  if (!token) {
    alert("Please complete the CAPTCHA");
    return;
  }

  // 2) verify it via your Cloud Function
  const ok = await verifyCaptcha(token);
  if (!ok) {
    alert("CAPTCHA failed – please try again.");
    window.turnstile.reset();   // reset the widget for another try
    return;
  }

  // 3) now attempt Firebase sign‑in
  try {
    const { user: loggedInUser } = await signInWithEmailAndPassword(auth, email, password);

    // 4) if not Gmail, enforce emailVerification
    if (
      !email.toLowerCase().endsWith("@gmail.com") &&
      !loggedInUser.emailVerified
    ) {
      alert("Please verify your email before signing in.");
      await logout();
    }
  } catch (error) {
    alert(error.message);
  }
};


  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (isAppSubdomain && isMobile) {
    return (
      <div className="mobile-warning">
        <h1>📱 Oops!</h1>
        <p>
          This part of EditVibe only works on desktop right now.
          <br />
          Please switch to a desktop browser.
        </p>
      </div>
    );
  }

// ============================
// Sign Up with Email
// ============================

// Step 1: Email only (no password)
const handleSignUpEmailOnly = () => {
  if (!email) {
    alert("Please enter an email.");
    return;
  }
  if (!isValidEmail(email)) {
    alert(
      "Please enter a valid email address from allowed providers (e.g. gmail.com, yahoo.com, outlook.com)."
    );
    return;
  }
  setSignUpStep(2);
};

// Step 2: Set password (email locked)
const handleEmailSignUpFinal = async () => {
  if (!password) {
    alert("Please enter a password.");
    return;
  }

  // ─── Turnstile ────────────────────────────────────
  const token = getTurnstileToken();
  if (!token) {
    alert("Please complete the CAPTCHA");
    return;
  }
  const ok = await verifyCaptcha(token);
  if (!ok) {
    alert("CAPTCHA failed – please try again.");
    window.turnstile.reset();
    return;
  }
  // ──────────────────────────────────────────────────

  try {
    const { user: newUser } = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    // ─── NEW: create their Firestore profile just like onAuthStateChanged ───
    const userDocRef = doc(db, "users", newUser.uid);
    await setDoc(userDocRef, {
      credits: 1150,
      plan: "free",
      createdAt: serverTimestamp(),
      email: newUser.email,
      userId: newUser.uid,
      hasSeenTour: false
    });
    console.log("New email‐signup user doc created."); 

    // If it's not Gmail, send a verification email and immediately sign out:
    if (!email.toLowerCase().endsWith("@gmail.com")) {
      await sendEmailVerification(newUser);
      alert(
        "A verification email has been sent. Please verify your email before signing in."
      );
      await logout();
    }
    // Gmail addresses bypass email‑verification.
  } catch (error) {
    alert(error.message);
  }
};

  if (loading) {
    return <div style={{ textAlign: "center", marginTop: "50px" }}>Loading...</div>;
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            !isAppSubdomain
              // on the root domain serve your marketing/front page
              ? <EditVibeMainPage />
              // on app.editvibe.ai run your existing login/app flow
              : (
                  user ? (
                    <EditVibeLayout user={user} userData={userData} logout={logout} />
                  ) : (
                    <div className="signin-page">
                      <div className="logo-top-left">
                        <img src={editvibelogo1} alt="Edit Vibe Logo" className="signin-logo" />
                      </div>

                      <div className="signin-container">
                        <h2 className="signin-title">
                          {isSignUp ? "Create an account" : "Welcome back"}
                        </h2>

                        {isSignUp && (
                          <>
                            {signUpStep === 1 && (
                              <>
                                <input
                                  type="email"
                                  placeholder="Email address"
                                  className="signin-input"
                                  value={email}
                                  onChange={(e) => setEmail(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSignUpEmailOnly();
                                    }
                                  }}
                                />
                                <div style={{ height: "1px", marginBottom: "1rem" }}></div>
                                <button className="continue-btn" onClick={handleSignUpEmailOnly}>
                                  Continue
                                </button>
                                <p className="signup-text">
                                  Already have an account?{" "}
                                  <a
                                    href="#0"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setIsSignUp(false);
                                    }}
                                  >
                                    Sign in
                                  </a>
                                </p>
                                <div className="divider-or">or</div>
                                <button className="social-btn google" onClick={signInWithGoogle}>
                                  Continue with Google
                                </button>
                                <div className="login-links">
  {/* if you have a TermsOfUse route/component in place */}
  <Link to="/terms-of-service">Terms of Service</Link> |{' '}
  <Link to="/privacy-policy">Privacy Policy</Link>
</div>
                              </>
                            )}

                            {signUpStep === 2 && (
                              <>
                                <input
                                  type="email"
                                  className="signin-input"
                                  value={email}
                                  disabled
                                  style={{ opacity: 0.6 }}
                                />
                                <input
                                  type="password"
                                  placeholder="Password"
                                  className="signin-input"
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleEmailSignUpFinal();
                                    }
                                  }}
                                />

                                {/* ——— Turnstile CAPTCHA ——— */}
                                <div className="turnstile-wrapper" style={{ margin: "1rem 0" }}>
                                <div
  id="cf-widget-signup"
  data-sitekey="0x4AAAAAABaV9SdFLBkhvmJ4"
></div>
                                </div>

                                <button className="continue-btn" onClick={handleEmailSignUpFinal}>
                                  Continue
                                </button>
                                <p
                                  style={{ marginTop: "16px", cursor: "pointer", color: "#666" }}
                                  onClick={() => setSignUpStep(1)}
                                >
                                  Go back
                                </p>
                                <div className="divider-or">or</div>
                                <button className="social-btn google" onClick={signInWithGoogle}>
                                  Continue with Google
                                </button>
                                <div className="login-links">
                                <Link to="/terms-of-service">Terms of Service</Link> |{' '}
                                <Link to="/privacy-policy">Privacy Policy</Link>
                                </div>
                              </>
                            )}
                          </>
                        )}

{!isSignUp && (
  <>
    {loginStep === 1 ? (
      <>
        <input
          type="email"
          placeholder="Email address"
          className="signin-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setLoginStep(2);
          }}
        />

        <button
          className="continue-btn"
          onClick={() => {
            if (!email) {
              alert("Please enter an email.");
            } else {
              setLoginStep(2);
            }
          }}
        >
          Continue
        </button>

        <p className="signup-text">
          Don't have an account?{" "}
          <a
            href="#0"
            onClick={(e) => {
              e.preventDefault();
              setIsSignUp(true);
              setSignUpStep(1);
            }}
          >
            Sign up
          </a>
        </p>

        <div className="divider-or">or</div>
        <button className="social-btn google" onClick={signInWithGoogle}>
          Continue with Google
        </button>
        <div className="login-links">
        <Link to="/terms-of-service">Terms of Service</Link> |{' '}
        <Link to="/privacy-policy">Privacy Policy</Link>
</div>
      </>
    ) : (
      <>
        <input
          type="email"
          className="signin-input"
          value={email}
          disabled
          style={{ opacity: 0.6 }}
        />

        <input
          type="password"
          placeholder="Password"
          className="signin-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleEmailLogin();
          }}
        />

        {/* ——— Turnstile CAPTCHA ——— */}
        <div className="turnstile-wrapper" style={{ margin: "1rem 0" }}>
          <div
            id="cf-widget-login"
            data-sitekey="0x4AAAAAABaV9SdFLBkhvmJ4"
          />
        </div>

        <button className="continue-btn" onClick={handleEmailLogin}>
          Continue
        </button>
        <p
          style={{ marginTop: "16px", cursor: "pointer", color: "#666" }}
          onClick={() => setLoginStep(1)}
        >
          Go back
        </p>

        <div className="divider-or">or</div>
        <button className="social-btn google" onClick={signInWithGoogle}>
          Continue with Google
        </button>
        <div className="login-links">
          <a href="#0">Terms of Use</a> | <a href="#0">Privacy Policy</a>
        </div>
      </>
    )}
  </>
)}
                      </div>
                    </div>
                  )
                )
          }
        />

        <Route
          path="/admin"
          element={
            user && user.email === "editvibedan@gmail.com" ? (
              <AdminDashboard user={user} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* Stripe success page */}
        <Route path="/purchase-success" element={<PurchaseSuccess user={user} />} />

        <Route path="/about" element={<AboutPage />} />
{/* Privacy Policy page */}
<Route path="/terms-of-service" element={<TermsOfService />} />
   <Route
     path="/privacy-policy"
     element={<PrivacyPolicy />}
   />
        {/* EditVibeTool route */}
        <Route path="/editvibetool" element={<EditVibeTool user={user} userData={userData} logout={logout} />} />

        <Route
          path="/EditVibeMainPage"
          element={<EditVibeMainPage />}
        />
      </Routes>
    </Router>
  );
};

export default App;
